/**
 * "Add to Home Screen" prompt for MSRs (owner, 2026-10-30): field reps should open the portal like an app.
 *
 * Pure helpers (unit-tested) plus a module-level capture of Chrome's `beforeinstallprompt`, which can fire before
 * React mounts the banner. The banner itself is MsrInstallPrompt.
 *
 * Rules: msr role only; phones/tablets only; never when already running as the installed app; dismissible, and a
 * dismissal is remembered on this device for 30 days (localStorage, every access guarded).
 */

export type InstallPlatform = "ios" | "android" | "desktop";

export const INSTALL_DISMISS_KEY = "msr-install-prompt-dismissed-at";
export const INSTALL_DISMISS_DAYS = 30;

/** Chrome's install event (not in the TS DOM lib). */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** iPhone/iPod/iPad (iPadOS reports a Mac user agent, so a touch "Mac" counts as iOS). Android. Else desktop. */
export function detectPlatform(
  userAgent: string,
  maxTouchPoints = 0,
): InstallPlatform {
  const ua = userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return "ios";
  }
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) {
    return "ios";
  }
  if (/Android/i.test(ua)) {
    return "android";
  }
  return "desktop";
}

/** Already opened from the home screen (Chrome/Android display-mode, or iOS navigator.standalone). */
export function isRunningStandalone(win: Window = window): boolean {
  try {
    if (win.matchMedia?.("(display-mode: standalone)").matches) {
      return true;
    }
  } catch {
    /* matchMedia unavailable */
  }
  return (
    (win.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** True while a dismissal on this device is younger than 30 days. Unreadable storage = not dismissed. */
export function isInstallPromptDismissed(
  now: number = Date.now(),
  storage: Storage | null = safeStorage(),
): boolean {
  try {
    const raw = storage?.getItem(INSTALL_DISMISS_KEY);
    if (!raw) {
      return false;
    }
    const at = Number(raw);
    if (!Number.isFinite(at)) {
      return false;
    }
    return now - at < INSTALL_DISMISS_DAYS * 86_400_000 && at <= now;
  } catch {
    return false;
  }
}

export function recordInstallPromptDismissed(
  now: number = Date.now(),
  storage: Storage | null = safeStorage(),
): void {
  try {
    storage?.setItem(INSTALL_DISMISS_KEY, String(now));
  } catch {
    /* private mode / blocked storage: the banner just comes back next visit */
  }
}

export function shouldShowInstallPrompt(args: {
  role: string | null | undefined;
  platform: InstallPlatform;
  standalone: boolean;
  dismissed: boolean;
}): boolean {
  return (
    (args.role ?? "").toLowerCase() === "msr" &&
    args.platform !== "desktop" &&
    !args.standalone &&
    !args.dismissed
  );
}

// ---- beforeinstallprompt capture (module level, so an early event is not lost) ----

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let installed = false;

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

export function clearDeferredInstallPrompt(): void {
  deferred = null;
  listeners.forEach((l) => l());
}

export function wasAppInstalled(): boolean {
  return installed;
}

export function subscribeInstallPrompt(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Call once at startup. Holds Chrome's install event so the MSR banner can offer it; other roles never see it. */
export function captureInstallPrompt(win: Window = window): void {
  try {
    win.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      deferred = e as BeforeInstallPromptEvent;
      listeners.forEach((l) => l());
    });
    win.addEventListener("appinstalled", () => {
      installed = true;
      deferred = null;
      listeners.forEach((l) => l());
    });
  } catch {
    /* never block startup */
  }
}
